import React, { useEffect, useState } from "react";
import { Save, Loader2, KeyRound, CreditCard, Mail, Trash2, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import AdminShell from "./AdminShell";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [form, setForm] = useState({
    adyen_api_key: "",
    adyen_hmac_key: "",
    adyen_client_key: "",
    adyen_merchant_account: "",
    adyen_environment: "test",
    adyen_return_url: "",
    payments_enabled: false,
    mailco_api_key: "",
    mailco_from_email: "",
    mailco_from_name: "",
    mailco_admin_alert_email: "",
    mailco_template: "",
    emails_enabled: true,
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setForm((f) => ({
        ...f,
        adyen_client_key: data.adyen_client_key || "",
        adyen_merchant_account: data.adyen_merchant_account || "",
        adyen_environment: data.adyen_environment || "test",
        adyen_return_url: data.adyen_return_url || "",
        payments_enabled: !!data.payments_enabled,
        mailco_from_email: data.mailco_from_email || "",
        mailco_from_name: data.mailco_from_name || "",
        mailco_admin_alert_email: data.mailco_admin_alert_email || "",
        mailco_template: data.mailco_template || "",
        emails_enabled: !!data.emails_enabled,
      }));
      setTestTo((prev) => prev || data.mailco_admin_alert_email || "");
    } catch (e) {
      toast.error(formatApiError(e, "Could not load settings"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    setSaving(true);
    // Send only fields the admin has changed / filled
    const payload = { ...form };
    const sentMailcoKey = Boolean(payload.mailco_api_key);
    // Secret fields: only send when non-empty (empty means "leave as is")
    for (const k of ["adyen_api_key", "adyen_hmac_key", "mailco_api_key"]) {
      if (!payload[k]) delete payload[k];
    }
    try {
      await api.patch("/admin/settings", payload);
      toast.success(
        sentMailcoKey
          ? "Settings saved. Scroll to Send test email (this page) to verify Mailco."
          : "Settings saved"
      );
      setForm((f) => ({ ...f, adyen_api_key: "", adyen_hmac_key: "", mailco_api_key: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  async function clearSecret(field) {
    if (!window.confirm(`Clear the stored ${field}? Payments/emails using this key will stop until you enter a new one.`)) return;
    try {
      await api.delete(`/admin/settings/secret/${field}`);
      toast.success(`${field} cleared`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not clear"));
    }
  }

  async function sendTestEmail() {
    const to = testTo.trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a recipient email first");
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/settings/test-email", { to_email: to });
      const queued = data?.id ? ` Mailco id ${data.id}.` : "";
      const toggleNote =
        data?.emails_enabled === false
          ? " Transactional emails are still off, so live leads/checkouts will not send until you enable the toggle."
          : "";
      toast.success(`Test email queued to ${data?.to || to}.${queued}${toggleNote}`);
    } catch (e) {
      toast.error(formatApiError(e, "Test email failed"));
    } finally {
      setSendingTest(false);
    }
  }

  if (loading || !settings) {
    return (
      <AdminShell>
        <div className="p-8 text-sm text-zinc-500">Loading...</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-oj-ink">Settings</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            Integration keys and feature flags. Secrets are encrypted at rest. CMS copy (terms, privacy, about, pricing, configurator) is under{" "}
            <a href="/admin/pages" className="text-oj-ink font-semibold underline underline-offset-2">Pages</a>, not here.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-save-settings"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-oj-ink text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-60"
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save settings
        </button>
      </div>

      <div className="space-y-6">
        <section className="rounded-2xl border border-zinc-200 bg-white p-6">
          <h3 className="text-base font-display font-bold text-oj-ink">How admin login works</h3>
          <ul className="mt-3 text-sm text-zinc-600 space-y-2 list-disc pl-5">
            <li>
              One login for inbox, CMS pages, Offers, vouchers, and these settings:{" "}
              <code className="text-oj-ink">https://ojavana.com/admin</code> (email + password).
              After login, left menu: Inbox · Offers · Pages · Settings · Vouchers · Leads.
            </li>
            <li>
              Account comes from <code className="text-oj-ink">ADMIN_EMAIL</code> and{" "}
              <code className="text-oj-ink">ADMIN_INITIAL_PASSWORD</code> in the server{" "}
              <code>.env</code>. Changing those and restarting <code>ojavana-backend</code> reseeds
              the account. Cookie: <code className="text-oj-ink">oj_admin_token</code>.
            </li>
            <li>
              <code className="text-oj-ink">ADMIN_TOKEN</code> is obsolete for the browser. Do not
              paste a token; save the email and password instead.
            </li>
          </ul>
        </section>

        {/* Mailco EES — first so Send test email is above the fold */}
        <section className="rounded-2xl border-2 border-oj-brand/40 bg-white p-6" id="mailco">
          <div className="flex items-center gap-2">
            <Mail className="size-5 text-oj-brand" />
            <h3 className="text-base font-display font-bold text-oj-ink">Mailco EES — Transactional email</h3>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Paste a <code>mail_live_…</code> key, click Save settings, then Send test email on this same page.
            Dashboard:{" "}
            <a href="https://ees.mailco.ch" target="_blank" rel="noreferrer" className="text-oj-brand hover:underline inline-flex items-center gap-0.5">
              ees.mailco.ch <ExternalLink className="size-3" />
            </a>
            . From address must use a verified Mailco domain.{" "}
            <a href="https://mailco.ch/docs" target="_blank" rel="noreferrer" className="text-oj-brand hover:underline inline-flex items-center gap-0.5">
              Docs <ExternalLink className="size-3" />
            </a>
          </p>
          <div className="mt-5 rounded-xl border-2 border-oj-brand bg-oj-brand/10 p-4" id="mailco-test-email">
            <h4 className="text-base font-display font-bold text-oj-ink">Send a Mailco test email</h4>
            <p className="mt-1 text-sm text-zinc-700">
              This is the test button. Save the API key first, then send one real Mailco message to yourself.
              The send uses raw subject/HTML (no Mailco dashboard template). Live lead and checkout emails still follow the toggle below.
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className={inputCls}
                placeholder="you@example.com"
                data-testid="mailco-test-email-to"
              />
              <button
                type="button"
                data-testid="mailco-send-test-email"
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="inline-flex items-center justify-center gap-2 shrink-0 bg-oj-brand text-oj-deep text-sm font-bold px-5 py-3 rounded-md hover:opacity-90 disabled:opacity-60"
              >
                {sendingTest ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                Send test email
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <SecretField
              label="Mailco API key (override .env MAILCO_API_KEY)"
              placeholder={settings.mailco_api_key_set ? "•••••••••••••••••• (stored)" : "mail_live_…"}
              stored={settings.mailco_api_key_set}
              value={form.mailco_api_key}
              onChange={(v) => set("mailco_api_key", v)}
              onClear={() => clearSecret("mailco_api_key")}
              testId="mailco-api-key"
            />
            <Field label="From email">
              <input value={form.mailco_from_email} onChange={(e) => set("mailco_from_email", e.target.value)} className={inputCls} placeholder="hello@ojavana.com" />
            </Field>
            <Field label="From name">
              <input value={form.mailco_from_name} onChange={(e) => set("mailco_from_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Admin alert email">
              <input value={form.mailco_admin_alert_email} onChange={(e) => set("mailco_admin_alert_email", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Mailco template slug (optional)">
              <input
                value={form.mailco_template}
                onChange={(e) => set("mailco_template", e.target.value)}
                className={inputCls}
                placeholder="leave empty — raw HTML send"
              />
            </Field>
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            Leave blank to send subject + HTML + text with no dashboard template (current EES default).
            A slug is optional. If you need one, start with <code>test-email</code> or <code>test</code>.
            Published names EES has listed: <code>order-confirmation</code>, <code>order-shipped</code>,{" "}
            <code>payment-link</code>, <code>test</code>, <code>test-email</code>, <code>transaction</code>,{" "}
            <code>transaction-receipt</code>. Do not use <code>transactional</code> — it is not published.
            Key scopes: <code>messages:write</code>.
          </p>
          <Toggle
            label="Send transactional emails"
            checked={form.emails_enabled}
            onChange={(v) => set("emails_enabled", v)}
            testId="emails-enabled"
          />
        </section>

        {/* Adyen */}
        <section className="rounded-2xl border border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <CreditCard className="size-5 text-oj-brand" />
            <h3 className="text-base font-display font-bold text-oj-ink">Adyen — Card / Apple Pay / Twint</h3>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Get keys from Adyen Customer Area → Developers → API credentials (API key) and Developers → Webhooks (HMAC key). {" "}
            <a href="https://ca-live.adyen.com/" target="_blank" rel="noreferrer" className="text-oj-brand hover:underline inline-flex items-center gap-0.5">
              Open Adyen <ExternalLink className="size-3" />
            </a>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <SecretField
              label="API key (server)"
              placeholder={settings.adyen_api_key_set ? "•••••••••••••••••• (stored)" : "AQEmhmfxK4..."}
              stored={settings.adyen_api_key_set}
              value={form.adyen_api_key}
              onChange={(v) => set("adyen_api_key", v)}
              onClear={() => clearSecret("adyen_api_key")}
              testId="adyen-api-key"
            />
            <SecretField
              label="HMAC signing key (webhook)"
              placeholder={settings.adyen_hmac_key_set ? "•••••••••••••••••• (stored)" : "Hex string from Adyen webhook"}
              stored={settings.adyen_hmac_key_set}
              value={form.adyen_hmac_key}
              onChange={(v) => set("adyen_hmac_key", v)}
              onClear={() => clearSecret("adyen_hmac_key")}
              testId="adyen-hmac-key"
            />
            <Field label="Merchant account">
              <input value={form.adyen_merchant_account} onChange={(e) => set("adyen_merchant_account", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Client key">
              <input value={form.adyen_client_key} onChange={(e) => set("adyen_client_key", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Environment">
              <select value={form.adyen_environment} onChange={(e) => set("adyen_environment", e.target.value)} className={inputCls}>
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </Field>
            <Field label="Return URL (optional)">
              <input value={form.adyen_return_url} onChange={(e) => set("adyen_return_url", e.target.value)} className={inputCls} placeholder="Auto-derived if empty" />
            </Field>
          </div>
          <Toggle
            label="Enable online payments (show Buy now on offers)"
            checked={form.payments_enabled}
            onChange={(v) => set("payments_enabled", v)}
            testId="payments-enabled"
          />
          <div className="mt-4 rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
            <strong>Webhook URL to configure in Adyen:</strong>{" "}
            <code className="text-oj-ink">{(process.env.REACT_APP_BACKEND_URL || "") + "/api/webhooks/adyen"}</code>
          </div>
        </section>
      </div>
    </AdminShell>
  );
}

const inputCls =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-oj-brand";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="mt-4 flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 accent-oj-brand"
      />
      <span className="text-sm text-zinc-700">{label}</span>
    </label>
  );
}

function SecretField({ label, placeholder, stored, value, onChange, onClear, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1">
          <KeyRound className="size-3" /> {label}
        </span>
        {stored && (
          <button type="button" onClick={onClear} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
            <Trash2 className="size-3" /> Clear
          </button>
        )}
      </div>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`mt-1.5 ${inputCls}`}
      />
      <p className="mt-1 text-xs text-zinc-400">
        {stored ? "Leave blank to keep the existing value." : "Not yet configured."}
      </p>
    </div>
  );
}
